import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { openOrDownloadBlob } from '~/modules/covenants/utils.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import {
  selectGuaranteesApplicationTimeline,
  selectGuaranteesApplicationTimelineStatus,
} from '~/store/guarantees/selectors.ts';
import { fetchGuaranteesApplicationTimeline } from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  GuaranteesApplicationRouteId,
  GuaranteesFetchStatus,
  GuaranteesFileType,
  type TGuaranteesApplicationFileCapability,
} from '~/store/guarantees/types.ts';
import { useGuaranteesApplicationFilesCapabilities } from '../../../shared/components/ApplicationFiles/useGuaranteesApplicationFilesCapabilities.ts';
import type {
  TGuaranteesApplicationFileActionHandler,
  TGuaranteesApplicationFileUploadHandler,
} from '../../../shared/components/ApplicationFiles/types.ts';
import {
  findApplicationFileCapability,
  getApplicationFileId,
} from '../../../shared/components/ApplicationFiles/utils.ts';
import {
  notifyGuaranteesApplicationFilesChanged,
  useGuaranteesApplicationFilesSync,
} from './applicationFilesSync.ts';
import type { IGuaranteesManagerApplicationSidePanelParams } from './types.ts';

const GUARANTEES_APPLICATION_FILE_FALLBACK_NAME = 'application-file';
const AM_OTHER_DOCUMENT_DISPLAY_NAME = 'Додаткові документи';

export const getManagerApplicationFileTypes = (
  fileTypes: TGuaranteesApplicationFileCapability[]
): TGuaranteesApplicationFileCapability[] => {
  const additionalDocumentsCapability: TGuaranteesApplicationFileCapability = {
    fileType: GuaranteesFileType.AM_OTHER_DOCUMENT,
    displayName: AM_OTHER_DOCUMENT_DISPLAY_NAME,
    isMultipleFiles: true,
    canDownload: true,
    canUpload: true,
    canRegenerate: false,
    canDelete: true,
    files: [],
  };
  const hasAdditionalDocuments = fileTypes.some(
    ({ fileType }) => fileType === GuaranteesFileType.AM_OTHER_DOCUMENT
  );

  return hasAdditionalDocuments
    ? fileTypes.map((capability) =>
        capability.fileType === GuaranteesFileType.AM_OTHER_DOCUMENT
          ? { ...capability, ...additionalDocumentsCapability, files: capability.files ?? [] }
          : capability
      )
    : [...fileTypes, additionalDocumentsCapability];
};

export const useGuaranteesManagerApplicationSidePanel = ({
  applicationId,
  onError,
}: IGuaranteesManagerApplicationSidePanelParams) => {
  const dispatch = useAppDispatch();
  const handledApplicationFilesErrorRef = useRef<unknown>(null);
  const timelineEvents = useSelector(selectGuaranteesApplicationTimeline);
  const timelineStatus = useSelector(selectGuaranteesApplicationTimelineStatus);
  const effectiveApplicationId =
    applicationId && applicationId !== GuaranteesApplicationRouteId.New ? applicationId : null;
  const applicationFiles = useGuaranteesApplicationFilesCapabilities(effectiveApplicationId);
  useGuaranteesApplicationFilesSync(effectiveApplicationId, () => {
    void applicationFiles.refresh().catch(onError);
  });
  const fileTypes = useMemo(
    () =>
      effectiveApplicationId ? getManagerApplicationFileTypes(applicationFiles.fileTypes) : [],
    [applicationFiles.fileTypes, effectiveApplicationId]
  );

  useEffect(() => {
    if (!effectiveApplicationId) {
      return;
    }

    void dispatch(fetchGuaranteesApplicationTimeline({ applicationId: effectiveApplicationId }))
      .unwrap()
      .catch(onError);
  }, [dispatch, effectiveApplicationId, onError]);

  useEffect(() => {
    if (applicationFiles.error) {
      if (handledApplicationFilesErrorRef.current === applicationFiles.error) {
        handledApplicationFilesErrorRef.current = null;
        return;
      }

      onError(applicationFiles.error);
    }
  }, [applicationFiles.error, onError]);

  const handleUpload = useCallback<TGuaranteesApplicationFileUploadHandler>(
    async (fileType, multipartFile, file) => {
      try {
        await applicationFiles.upload({
          fileId:
            fileType === GuaranteesFileType.AM_OTHER_DOCUMENT
              ? undefined
              : (getApplicationFileId(file) ?? undefined),
          fileType,
          multipartFile,
        });
        notifyGuaranteesApplicationFilesChanged(effectiveApplicationId);
      } catch (error) {
        handledApplicationFilesErrorRef.current = error;
        onError(error);
      }
    },
    [applicationFiles, onError]
  );

  const handleRegenerate = useCallback<TGuaranteesApplicationFileActionHandler>(
    async (fileType, file) => {
      try {
        await applicationFiles.regenerate({
          fileId: getApplicationFileId(file) ?? undefined,
          fileType,
        });
        notifyGuaranteesApplicationFilesChanged(effectiveApplicationId);
      } catch (error) {
        handledApplicationFilesErrorRef.current = error;
        onError(error);
      }
    },
    [applicationFiles, onError]
  );

  const handleDownload = useCallback<TGuaranteesApplicationFileActionHandler>(
    async (fileType, file) => {
      const fileId = getApplicationFileId(file);

      if (!fileId) {
        return;
      }

      try {
        const downloadedDocument = await applicationFiles.download({
          fileId,
          fileType,
          isAttached: true,
        });

        if (!downloadedDocument) {
          return;
        }

        const capability = findApplicationFileCapability(fileTypes, fileType);
        const displayName = capability?.displayName?.trim() || null;

        openOrDownloadBlob({
          blob: downloadedDocument.data,
          fileName: displayName || downloadedDocument.filename,
          fallbackFileName: GUARANTEES_APPLICATION_FILE_FALLBACK_NAME,
          contentType: downloadedDocument.contentType,
          forceDownload: true,
        });
      } catch (error) {
        handledApplicationFilesErrorRef.current = error;
        onError(error);
      }
    },
    [applicationFiles, fileTypes, onError]
  );

  const handleDelete = useCallback<TGuaranteesApplicationFileActionHandler>(
    async (fileType, file) => {
      const fileId = getApplicationFileId(file);

      if (!fileId) {
        return;
      }

      try {
        await applicationFiles.delete({ fileId, fileType });
        notifyGuaranteesApplicationFilesChanged(effectiveApplicationId);
      } catch (error) {
        handledApplicationFilesErrorRef.current = error;
        onError(error);
      }
    },
    [applicationFiles, onError]
  );

  return {
    filesBlockProps: {
      activeAction: applicationFiles.activeAction,
      fileTypes,
      isLoading: applicationFiles.isLoading,
      onDelete: handleDelete,
      onDownload: handleDownload,
      onRegenerate: handleRegenerate,
      onUpload: handleUpload,
    },
    statusFlowProps: {
      events: timelineEvents,
      isLoading: timelineStatus === GuaranteesFetchStatus.Loading,
    },
  };
};
